/**
 * Canonical URL + JSON-LD helpers for prayer schedule pages.
 *
 * URL conventions (post-restructure, short URLs only):
 *   provinsi:    /jadwal-{variant}/{prov}/
 *   kota:        /jadwal-{variant}/{kota}/         (no prov)
 *   kecamatan:   /jadwal-{variant}/{kota}/{kec}/   (no prov)
 */

import type { Location } from "@/data/locations";
import { getProvinsi, findKotaBySlug } from "@/data/locations";
import { displayLocationTitle } from "@/lib/prayer/copy";

export type PrayerVariant = "sholat" | "imsak" | "buka-puasa";

const ORIGIN = "https://hari.co.id";

export function prayerPath(variant: PrayerVariant, loc: Location): string {
  const base = variant === "buka-puasa" ? "/jadwal-buka-puasa" : `/jadwal-${variant}`;
  if (loc.kind === "provinsi") return `${base}/${loc.slug}/`;
  if (loc.kind === "kota") return `${base}/${loc.slug}/`;
  return `${base}/${loc.parentSlug}/${loc.slug}/`;
}

export function prayerCanonical(variant: PrayerVariant, loc: Location): string {
  return ORIGIN + prayerPath(variant, loc);
}

const displayName = displayLocationTitle;

export function prayerJsonLd(variant: PrayerVariant, loc: Location) {
  const variantLabel = variant === "sholat" ? "Jadwal Sholat" : variant === "imsak" ? "Jadwal Imsakiyah" : "Jadwal Buka Puasa";
  const base = variant === "buka-puasa" ? "jadwal-buka-puasa" : `jadwal-${variant}`;
  const url = prayerCanonical(variant, loc);
  const province = getProvinsi(loc.provinceSlug);
  const parentKota = loc.kind === "kecamatan" && loc.parentSlug ? findKotaBySlug(loc.parentSlug) : null;

  // Build breadcrumb based on location kind, all using short URLs.
  const crumbs: { name: string; url: string }[] = [
    { name: "Beranda", url: `${ORIGIN}/` },
    { name: variantLabel, url: `${ORIGIN}/${base}/` },
  ];
  if (loc.kind === "provinsi") {
    crumbs.push({ name: loc.name, url });
  } else if (loc.kind === "kota") {
    if (province) crumbs.push({ name: province.name, url: `${ORIGIN}/${base}/${province.slug}/` });
    crumbs.push({ name: loc.name, url });
  } else {
    if (province) crumbs.push({ name: province.name, url: `${ORIGIN}/${base}/${province.slug}/` });
    if (parentKota) crumbs.push({ name: parentKota.name, url: `${ORIGIN}/${base}/${parentKota.slug}/` });
    crumbs.push({ name: loc.name, url });
  }

  return [
    {
      "@context": "https://schema.org",
      "@type": "WebPage",
      name: `${variantLabel} ${displayName(loc)}`,
      url,
      inLanguage: "id-ID",
      about: {
        "@type": "Place",
        name: displayName(loc),
        geo: { "@type": "GeoCoordinates", latitude: loc.lat, longitude: loc.lng },
        ...(province ? { containedInPlace: { "@type": "AdministrativeArea", name: province.name } } : {}),
      },
    },
    {
      "@context": "https://schema.org",
      "@type": "BreadcrumbList",
      itemListElement: crumbs.map((c, i) => ({
        "@type": "ListItem",
        position: i + 1,
        name: c.name,
        item: c.url,
      })),
    },
  ];
}
